import { useEffect, useId, type CSSProperties, type ReactNode } from "react";
import styled, { css } from "styled-components";

import { TooltipIconButton, type IconButtonSize } from "../IconButton";
import { CloseIcon } from "../../icons/glyphs";
import { controlFocusRing } from "../foundations/controlStyles";
import { yScrollbarStyles } from "../foundations/scrollbar";
import { isRenderableNode } from "../../utils/reactNode";
import { useModalClose, useModalLabelRegistry } from "./modalContext";

export interface ModalHeaderProps {
  /** 헤더 내용. 보통 `<Modal.Header.Start>` + `<Modal.Header.End>`. */
  children?: ReactNode;
  /** 헤더 하단 구분선 표시 여부. 기본 `true`. */
  divider?: boolean;
  /** header root에 적용. */
  className?: string;
  style?: CSSProperties;
}

export interface ModalHeaderSlotProps {
  /** header slot 내용. */
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
}

export interface ModalHeaderTitleProps {
  /** dialog 제목. `<h2>`로 렌더되고 Modal의 accessible name으로 자동 연결된다. */
  children: ReactNode;
  /** 타이틀 `<h2>`의 id. SSR-안정 id가 필요하거나 외부에서 직접 참조할 때만 명시한다. */
  titleId?: string;
  className?: string;
  style?: CSSProperties;
}

/**
 * 기본 닫기(X) 버튼. 클릭 시 Modal의 `onClose`를 호출한다(context 자동 배선 — 핸들러 주입 불필요).
 * Escape/backdrop 클릭과 동일 경로라 가드/confirm 로직이 `onClose` 한 곳에 모인다.
 * "닫기 외 다른 동작"은 이 버튼이 아니라 `Modal.Header.End`에 일반 `IconButton`을 합성한다.
 */
export interface ModalHeaderCloseProps {
  /** accessible name + 기본 툴팁 텍스트. 기본 `"Close"`. */
  label?: string;
  /** 툴팁 내용 override. 생략 시 `label` 사용. */
  tooltip?: ReactNode;
  /** 비활성화. */
  disabled?: boolean;
  /** 버튼 size. 기본 `"md"`. */
  size?: IconButtonSize;
  className?: string;
  style?: CSSProperties;
}

export interface ModalBodyProps {
  /** dialog 본문. 이 영역이 header/footer 사이에서 스크롤된다. */
  children?: ReactNode;
  /** Body 자체 스크롤 여부. 기본 `true`; `false`면 내부 페이지가 자기 스크롤을 소유하도록 overflow를 숨긴다. */
  scrollable?: boolean;
  /** Body 기본 여백 적용 여부. 기본 `true`; edge-to-edge 임베드 페이지는 `false`. */
  padding?: boolean;
  /** keyboard 접근. 기본은 `scrollable=true`면 `0`, `scrollable=false`면 `-1`; 특수 케이스에서 override. */
  tabIndex?: number;
  className?: string;
  style?: CSSProperties;
}

export interface ModalFooterProps {
  /** footer 액션들. 보통 우측 정렬 버튼 row. */
  children?: ReactNode;
  /** 푸터 상단 구분선 표시 여부. 기본 `true`. */
  divider?: boolean;
  className?: string;
  style?: CSSProperties;
}

const Root = styled.header<{ $divider: boolean }>`
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
  padding: 1rem 1.5rem;
  background-color: ${({ theme }) => theme.color.bg.surfaceRaised};
  ${({ $divider, theme }) =>
    $divider
      ? css`
          border-bottom: 1px solid ${theme.color.border.divider};
        `
      : ""}
`;

const StartRoot = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;

  > :not([data-c6-modal-title]) {
    flex-shrink: 0;
  }
`;

const EndRoot = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;

  > * {
    flex-shrink: 0;
  }
`;

const Title = styled.h2`
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: ${({ theme }) => theme.typography.size.title};
  font-weight: ${({ theme }) => theme.typography.weight.semibold};
  color: ${({ theme }) => theme.color.text.default};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BodyRoot = styled.div<{ $scrollable: boolean; $padding: boolean }>`
  flex: 1 1 auto;
  min-height: 0;
  padding: ${({ $padding }) => ($padding ? "1.5rem" : "0")};
  ${({ $scrollable }) =>
    $scrollable
      ? yScrollbarStyles
      : `
        overflow: hidden;
      `}

  /* tabIndex=0 스크롤 영역이라 키보드로 연 모달에선 initial focus가 여기 온다 — UA 기본
     outline 대신 공유 ring. 안쪽(-2px)이어야 dialog 모서리·header/footer에 잘리지 않는다. */
  &:focus-visible {
    ${controlFocusRing("-2px")}
  }
`;

const FooterRoot = styled.div<{ $divider: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-shrink: 0;
  padding: 1rem 1.5rem;
  background-color: ${({ theme }) => theme.color.bg.surfaceRaised};
  ${({ $divider, theme }) =>
    $divider
      ? css`
          border-top: 1px solid ${theme.color.border.divider};
        `
      : ""}
`;

function ModalHeaderRoot({ children, divider = true, className, style }: ModalHeaderProps) {
  return (
    <Root $divider={divider} className={className} style={style}>
      {children}
    </Root>
  );
}

function ModalHeaderStart({ children, className, style }: ModalHeaderSlotProps) {
  return (
    <StartRoot className={className} style={style}>
      {children}
    </StartRoot>
  );
}

function ModalHeaderEnd({ children, className, style }: ModalHeaderSlotProps) {
  return (
    <EndRoot className={className} style={style}>
      {children}
    </EndRoot>
  );
}

function ModalHeaderTitle({ children, titleId, className, style }: ModalHeaderTitleProps) {
  const generatedTitleId = useId();
  const hasTitle = isRenderableNode(children);
  const resolvedTitleId = hasTitle ? (titleId ?? generatedTitleId) : undefined;

  const registry = useModalLabelRegistry();
  useEffect(() => {
    if (!registry || resolvedTitleId === undefined) return;
    registry.register(resolvedTitleId);
    return () => registry.unregister(resolvedTitleId);
  }, [registry, resolvedTitleId]);

  return hasTitle ? (
    <Title id={resolvedTitleId} data-c6-modal-title className={className} style={style}>
      {children}
    </Title>
  ) : null;
}

function ModalHeaderClose({
  label = "Close",
  tooltip,
  disabled = false,
  size = "md",
  className,
  style,
}: ModalHeaderCloseProps) {
  // Modal의 onClose를 context로 받아 호출 — Escape/backdrop과 동일 경로. provider 밖이면 no-op.
  const close = useModalClose();
  return (
    <TooltipIconButton
      data-c6-modal-action="close"
      variant="ghost"
      size={size}
      tooltip={tooltip ?? label}
      aria-label={label}
      disabled={disabled}
      onClick={close ?? undefined}
      className={className}
      style={style}
    >
      <CloseIcon />
    </TooltipIconButton>
  );
}

function ModalBody({
  children,
  scrollable = true,
  padding = true,
  tabIndex,
  className,
  style,
}: ModalBodyProps) {
  const resolvedTabIndex = tabIndex ?? (scrollable ? 0 : -1);

  return (
    <BodyRoot
      $scrollable={scrollable}
      $padding={padding}
      tabIndex={resolvedTabIndex}
      className={className}
      style={style}
    >
      {children}
    </BodyRoot>
  );
}

function ModalFooter({ children, divider = true, className, style }: ModalFooterProps) {
  return (
    <FooterRoot $divider={divider} className={className} style={style}>
      {children}
    </FooterRoot>
  );
}

const HeaderTitle = Object.assign(ModalHeaderTitle, {
  displayName: "Modal.Header.Title",
});

const HeaderStart = Object.assign(ModalHeaderStart, {
  displayName: "Modal.Header.Start",
});

const HeaderEnd = Object.assign(ModalHeaderEnd, {
  displayName: "Modal.Header.End",
});

const HeaderClose = Object.assign(ModalHeaderClose, {
  displayName: "Modal.Header.Close",
});

export const ModalHeader = Object.assign(ModalHeaderRoot, {
  Start: HeaderStart,
  Title: HeaderTitle,
  End: HeaderEnd,
  Close: HeaderClose,
  displayName: "Modal.Header",
});

export const ModalBodyRoot = Object.assign(ModalBody, {
  displayName: "Modal.Body",
});

export const ModalFooterRoot = Object.assign(ModalFooter, {
  displayName: "Modal.Footer",
});
